// views/Admin/PushNotifications/PushNotifications.tsx
import { useEffect, useMemo, useState } from 'react';
import Card from '../../../components/common/Card/Card';
import Button from '../../../components/common/Button/Button';
import { PushNotificationManager, PushSubscription } from '../../../Entities';
import { notify } from '../../../components/common/Toast/toastBridge';
import styles from './PushNotifications.module.css';
import { useNavigate } from 'react-router-dom';
import Loading from '../../../components/common/Loading/Loading';

type RecipientMode = 'all' | 'specific';

const PushNotifications = () => {
  const [processing, setProcessing] = useState(false);
  const [header, setHeader] = useState('');
  const [body, setBody] = useState('');
  const [allSubscriptions, setAllSubscriptions] = useState<PushSubscription[]>([]);
  const [selectedSubscriptions, setSelectedSubscriptions] = useState<number[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [mode, setMode] = useState<RecipientMode>('all');
  const [search, setSearch] = useState('');
  const navigate = useNavigate();

  const getData = async (): Promise<void> => {
    try {
      setLoading(true);
      const response = await PushNotificationManager.getAll();
      setAllSubscriptions(response);
      setLoading(false);
    } catch (e: any) {
      notify.error("odběry se nepodařilo načíst");
      navigate("/dashboard");
    }
  }
  useEffect(() => {
    getData();
  }, []);

  const filteredSubscriptions = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return allSubscriptions;
    return allSubscriptions.filter((s) => s.user_name.toLowerCase().includes(q));
  }, [allSubscriptions, search]);

  const selectedSet = useMemo(() => new Set(selectedSubscriptions), [selectedSubscriptions]);

  const handleModeChange = (next: RecipientMode) => {
    setMode(next);
    if (next === 'all') {
      setSelectedSubscriptions([]);
    }
  };

  const toggleUser = (userId: number) => {
    setSelectedSubscriptions((prev) =>
      prev.includes(userId) ? prev.filter((id) => id !== userId) : [...prev, userId]
    );
  };

  const selectAllFiltered = () => {
    const ids = filteredSubscriptions.map((s) => s.user_id);
    setSelectedSubscriptions((prev) => Array.from(new Set([...prev, ...ids])));
  };

  const deselectAllFiltered = () => {
    const filteredIds = new Set(filteredSubscriptions.map((s) => s.user_id));
    setSelectedSubscriptions((prev) => prev.filter((id) => !filteredIds.has(id)));
  };

  const allFilteredSelected =
    filteredSubscriptions.length > 0 &&
    filteredSubscriptions.every((s) => selectedSet.has(s.user_id));

  const isSendDisabled =
    !header.trim() ||
    !body.trim() ||
    processing ||
    (mode === 'specific' && selectedSubscriptions.length === 0);

  const handleSend = async () => {
    if (!header.trim() || !body.trim()) return;
    if (mode === 'specific' && selectedSubscriptions.length === 0) return;
    try {
      setProcessing(true);
      const res: { sent_count: number } = await PushNotificationManager.send({
        header,
        body,
        receivers: mode === 'all' ? [] : selectedSubscriptions,
      });
      setHeader('');
      setBody('');
      notify.success(`Notifikace úspěšně odeslány do ${res.sent_count} zařízení`);
    } catch (error) {
      console.error(error);
      notify.error('Nepovedlo se odeslat Push notifikaci');
    } finally {
      setProcessing(false);
    }
  };

  if (loading) {
    return <Loading />
  }

  return (
    <div className={styles.pushNotificationsPage}>
      <div className={styles.header}>
        <h1>🔔 Správa push notifikací</h1>
      </div>

      <Card className={styles.tableCard}>
        <div className={styles.formGroup}>
          <label>Nadpis notifikace</label>
          <textarea
            className={styles.input}
            placeholder="Zadejte nadpis notifikace..."
            value={header}
            onChange={(e) => setHeader(e.target.value)}
            rows={2}
            maxLength={100}
          />
          <div className={styles.counter}>{header.length}/100</div>
        </div>

        <div className={styles.formGroup}>
          <label>Text notifikace</label>
          <textarea
            className={styles.input}
            placeholder="Zadejte text notifikace..."
            value={body}
            onChange={(e) => setBody(e.target.value)}
            rows={4}
            maxLength={300}
          />
          <div className={styles.counter}>{body.length}/300</div>
        </div>

        <div className={styles.formGroup}>
          <label>Příjemci</label>

          <div className={styles.modeToggle}>
            <button
              type="button"
              className={`${styles.modeOption} ${mode === 'all' ? styles.modeOptionActive : ''}`}
              onClick={() => handleModeChange('all')}
            >
              Všichni odběratelé
              {allSubscriptions.length > 0 && (
                <span className={styles.modeCount}>{allSubscriptions.length}</span>
              )}
            </button>
            <button
              type="button"
              className={`${styles.modeOption} ${mode === 'specific' ? styles.modeOptionActive : ''}`}
              onClick={() => handleModeChange('specific')}
            >
              Vybrat příjemce
            </button>
          </div>

          {mode === 'specific' && (
            <div className={styles.recipientsBox}>
              {loading ? (
                <div className={styles.emptyState}>Načítání odběratelů...</div>
              ) : allSubscriptions.length === 0 ? (
                <div className={styles.emptyState}>Žádní odběratelé nejsou k dispozici</div>
              ) : (
                <>
                  <div className={styles.listToolbar}>
                    <input
                      type="text"
                      className={styles.searchInput}
                      placeholder="Hledat uživatele..."
                      value={search}
                      onChange={(e) => setSearch(e.target.value)}
                    />
                    <button
                      type="button"
                      className={styles.linkButton}
                      onClick={allFilteredSelected ? deselectAllFiltered : selectAllFiltered}
                    >
                      {allFilteredSelected ? 'Zrušit výběr' : 'Vybrat vše'}
                    </button>
                  </div>

                  <div className={styles.userList}>
                    {filteredSubscriptions.length === 0 ? (
                      <div className={styles.emptyState}>Žádná shoda pro „{search}“</div>
                    ) : (
                      filteredSubscriptions.map((sub) => (
                        <label key={sub.user_id} className={styles.userItem}>
                          <input
                            type="checkbox"
                            className={styles.userCheckbox}
                            checked={selectedSet.has(sub.user_id)}
                            onChange={() => toggleUser(sub.user_id)}
                          />
                          <span>{sub.user_name}</span>
                        </label>
                      ))
                    )}
                  </div>

                  <div className={styles.selectedCount}>
                    Vybráno {selectedSubscriptions.length} z {allSubscriptions.length}
                  </div>
                </>
              )}
            </div>
          )}
        </div>

        <div className={styles.actions}>
          <Button onClick={handleSend} disabled={isSendDisabled}>
            Odeslat notifikaci
          </Button>
        </div>
      </Card>
    </div>
  );
};

export default PushNotifications;